import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type {
  LazyRowContainerScrollContainerAdapter,
  LazyRowContainerUpdateRangeFunction,
} from 'components/LazyRowContainer/LazyRowContainer';
import type React from 'react';
import { useEffect, useMemo, useRef } from 'react';
import type { Scrollbars, positionValues } from 'react-custom-scrollbars-2';

interface LazyRowContainerScrollViewAdapterHook {
  scrollViewProps: Pick<React.ComponentProps<typeof Scrollbars>, 'onUpdate'> & {
    ref: React.RefObject<Scrollbars>;
  };
  scrollContainerAdapter: LazyRowContainerScrollContainerAdapter;
}

const VIEWPORT_SIZE_POLL_INTERVAL = 300;

/**
 * @example
 * const adapter = useLazyRowContainerScrollViewAdapter();
 * return (
 *   <ScrollView {...adapter.scrollViewProps}>
 *     <LazyRowContainer
 *       {...props}
 *       scrollContainerAdapter={adapter.scrollContainerAdapter}
 *     />
 *   </ScrollView>
 * );
 */
export const useLazyRowContainerScrollViewAdapter =
  (): LazyRowContainerScrollViewAdapterHook => {
    const scrollViewRef = useRef<Scrollbars>(null);
    const updateRangeRef = useRef<LazyRowContainerUpdateRangeFunction>(null);

    const updateRange = useStableCallback(() => {
      if (scrollViewRef.current && updateRangeRef.current) {
        updateRangeRef.current(
          scrollViewRef.current.getScrollTop(),
          scrollViewRef.current.getClientHeight(),
        );
      }
    });

    useEffect(() => {
      updateRange();
      // ScrollView does not emit onUpdate when container dimensions change, so getting consistent view
      // is ensured by polling there. It is super cheap since LazyRowContainer remembers last dimensions
      // and skips the update on no change.
      const updateRangeInterval = window.setInterval(
        updateRange,
        VIEWPORT_SIZE_POLL_INTERVAL,
      );
      return () => {
        window.clearInterval(updateRangeInterval);
      };
    }, []);

    return useMemo(
      () => ({
        scrollViewProps: {
          ref: scrollViewRef,
          onUpdate: ({ scrollTop, clientHeight }: positionValues) => {
            updateRangeRef.current?.(scrollTop, clientHeight);
          },
        },
        scrollContainerAdapter: {
          updateRangeRef,
        },
      }),
      [],
    );
  };
